import React, { useState, createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * We use this context to help manage when we show the loading spinner in
 * LocationsContainer based on the call to geocodePlaceId.
 *
 * We introduced "jitter" in our call to geocodePlaceId so as to avoid
 * Google Maps API ratelimiting. As a result, we need to trigger a loading state
 * in LocationsContainer earlier than we normally do so that the diner doesn't perceive
 * the lag quite as clearly (i.e. without this, the user selects a location and
 * sees a noticeable delay in the application's response to their input).
 */
const GeocodeContext = createContext({
  isMakingGeocodeRequest: false,
  setIsMakingGeocodeRequest: () => {}
});

function GeocodeProvider({ children }) {
  const [isMakingGeocodeRequest, setIsMakingGeocodeRequest] = useState(false);

  const value = useMemo(
    () => ({
      isMakingGeocodeRequest,
      setIsMakingGeocodeRequest
    }),
    [isMakingGeocodeRequest]
  );

  return (
    <GeocodeContext.Provider value={value}>{children}</GeocodeContext.Provider>
  );
}

GeocodeProvider.propTypes = {
  children: PropTypes.node.isRequired
};

function useGeocode() {
  const context = useContext(GeocodeContext);
  if (context === undefined) {
    throw new Error('useGeocode must be used within a GeocodeContext');
  }

  return context;
}

export { GeocodeContext, GeocodeProvider, useGeocode };
